import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, Lock } from 'lucide-react';
import Seo from './Seo';

const Privacy = () => {
  const legalPhoneDisplay = '630-799-1556';
  const legalPhoneHref = '+16307991556';

  const [copied, setCopied] = useState(false);

  const copyLegalNumber = async () => {
    try {
      await navigator.clipboard.writeText(legalPhoneDisplay);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // ignore if clipboard fails
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Privacy Policy"
        description="Privacy Policy for Libsys Inc — how we collect, use, store, and protect your personal information. Covers cookies, data rights, retention, and contact information."
        path="/privacy-policy"
      />
      {/* Hero Header */}
      <section className="relative pt-20 pb-12 bg-primary overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-400/10 rounded-full blur-[120px] -mr-40 -mt-40 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px] -ml-20 -mb-20 pointer-events-none" />
        
        <div className="max-w-5xl mx-auto px-6 lg:px-12 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-[#e02424] text-[12px] font-black uppercase tracking-[0.2em] mb-6">
              <Eye className="w-4 h-4" />
              Privacy Centre
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white mb-4">
                Privacy <span className="text-secondary text-glow">Statement</span>
            </h1>
            <p className="text-blue-100/75 text-base md:text-lg max-w-2xl mx-auto font-medium leading-relaxed">
              Your privacy is our priority. Learn how we protect and manage the information you share with Libsys Inc.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-20 bg-slate-50/60">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-none"
          >
            {[
              {
                title: "Introduction",
                content: "Libsys believes strongly in defending the privacy of the personally identifiable information you share with us. We also believe it is important to inform you about how we will use your personal data, and to give you choices about how those data will be used. Our privacy policy may change from time to time. Please check the policy each time you use our Web Site for the most current information. This privacy policy applies to all information submitted on the website of Libsys Inc., and its subsidiaries."
              },
              {
                title: "Scope of this Privacy Policy",
                content: "In general, this Privacy Policy applies to all personal information of consumers collected or used by Libsys. This includes information from websites for commercial use that are owned by Libsys and offline collections of personal data for commercial use owned by Libsys. However, some of our websites or data collections work with special kinds of data (for example, medical data) which require a somewhat different privacy policy. Whenever and wherever that is the case, Libsys will make it clear that the privacy policy concerned is different from this general Libsys Privacy Policy."
              },
              {
                title: "Why share your personal data with Libsys?",
                content: (
                  <div className="space-y-4">
                    <p>Sharing your personally identifiable information enables us to offer you a number of benefits. It will make it easier and more convenient for you to:</p>
                    <ul className="list-disc pl-6 space-y-2 text-slate-600">
                      <li>Participate in our business process;</li>
                      <li>Request information specific to your interests;</li>
                      <li>Receive personalized messages and special opportunities that are relevant to your interests;</li>
                      <li>Save time by storing your preferences;</li>
                    </ul>
                    <p>Sometimes Libsys obtains your personal data from other parties outside Libsys. This includes the buying of contact information, of which you already are a customer or the disclosure of your personal data by one of our partner companies.</p>
                  </div>
                )
              },
              {
                title: "Sharing Information",
                content: (
                  <div className="space-y-4">
                    <p>We want to know you better so we can serve you better! You should know that your personally identifiable information may be transferred from your home country to other Libsys subsidiaries around the world. If you do not want us to send you personalized offers or information, you can let us know by sending us an email.</p>
                    <p>Libsys will only share your data with third parties outside Libsys in the following circumstances:</p>
                    <ul className="list-disc pl-6 space-y-2 text-slate-600 font-medium">
                      <li>Joint marketing efforts with partner companies (subject to their respective privacy policies).</li>
                      <li>Outside companies/individuals for the purpose of processing your data on behalf of Libsys.</li>
                      <li>Business transfers or acquisition of ownership.</li>
                      <li>Legal compliance and law enforcement requirements.</li>
                    </ul>
                  </div>
                )
              },
              {
                title: "Safeguarding Information",
                content: "We recognize our responsibility to protect the information you entrust to us. Libsys uses a variety of secure techniques to protect your information, including secure servers, firewalls, and encryption of financial data."
              },
              {
                title: "Cookies",
                content: "In order to provide better service or to address security hazards, we may occasionally use a “cookie”. A cookie is a small piece of information which a Web site stores on customer’s web browser on customer’s PC and can later retrieve. Most cookies last only through a single session, or visit to our site. None will contain information that will enable anyone to contact customer via telephone, e-mail, or any other means."
              },
              {
                title: "Use of I.P. Addresses",
                content: "Web servers automatically identify your computer by the IP address assigned to it during your session online. We may collect I.P. addresses for data collection. We do not link a user’s I.P. address to a person’s personal information, meaning each user’s session remains anonymous to us unless linked via domain database management."
              },
              {
                title: "Contacting Us",
                content: (
                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-8 mt-8">
                        <p className="font-bold text-slate-900 mb-4">You may contact us if you would like to ask any questions regarding our privacy policy or request a copy of your personal data.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                              <p className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-1">Privacy Support Email</p>
                              <a href="mailto:Info@LibsysInc.Com" className="text-primary font-bold hover:text-secondary transition-colors text-lg">Info@LibsysInc.Com</a>
                          </div>
                          <div>
                              <p className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">Legal Support Call</p>
                              <div className="flex flex-wrap gap-3">
                                <a
                                  href={`tel:${legalPhoneHref}`}
                                  className="px-4 py-2 bg-primary text-white font-black text-[11px] uppercase tracking-wider rounded-lg hover:bg-primary/90 transition-all"
                                >
                                  Call {legalPhoneDisplay}
                                </a>
                                <button
                                  type="button"
                                  onClick={copyLegalNumber}
                                  className="px-4 py-2 bg-white text-primary border border-primary/20 font-black text-[11px] uppercase tracking-wider rounded-lg hover:bg-primary/5 transition-all w-36"
                                >
                                  {copied ? 'Copied!' : 'Copy Number'}
                                </button>
                              </div>
                              <p className="text-slate-400 text-xs mt-2">
                                If call pop-up does not appear on desktop, copy and dial manually.
                              </p>
                          </div>
                        </div>
                    </div>
                )
              }
            ].map((section, i) => (
              <div key={i} className="mb-6 last:mb-0 bg-white border border-slate-100 rounded-2xl p-6 md:p-8 shadow-sm">
                <h2 className="text-xl md:text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
                  <span className="w-1.5 h-6 bg-secondary rounded-full" />
                  {section.title}
                </h2>
                <div className="text-slate-600 text-[16px] md:text-[17px] leading-8 font-medium pl-4">
                  {section.content}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Security Banner */}
      <section className="bg-primary py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center relative z-10">
            <Lock className="w-12 h-12 text-secondary mx-auto mb-6" />
            <h2 className="text-3xl font-black text-white mb-4">Data Security is our Promise</h2>
            <p className="text-blue-100/60 max-w-xl mx-auto text-lg font-medium mb-10">
                We employ industry-leading encryption and security protocols to ensure your data remains your own.
            </p>
            <div className="h-[1px] w-40 bg-secondary/30 mx-auto" />
        </div>
      </section>
    </div>
  );
};

export default Privacy;
